
  import { defineComponent, PropType } from 'vue';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';

  import { CODE_SEPARATORS } from '@/modules/ved/constants/fts/codes';
  import { splitCodes } from '@/modules/ved/utils/fts/code';

  import { TFTSWeeklyFilter, TFTSWeeklyFilterRequest } from '../../../types/fts-weekly';
  import { TExportConfig } from '../../common/ved-aggregated-export-buttons/types';
  import FTSFilterWrapper from '../fts-filter/FTSFilterWrapper.vue';
  import FTSFilterWrapperToogle from '../fts-filter/FTSFilterWrapperToogle.vue';
  import FTSWeeklyPeriodField from '../fts-weekly/FTSWeeklyPeriodField.vue';
  import FTSAdditionalUnitsField from '../FTSAdditionalUnitsField.vue';
  import FTSCountriesFilter from '../FTSCountriesFilter.vue';
  import FTSCumulativeTotalField from '../FTSCumulativeTotalField.vue';
  import FTSOkatoFilter from '../FTSOkatoFilter.vue';
  import FTSPriceField from '../FTSPriceField.vue';
  import FTSTradeDirectionField from '../FTSTradeDirectionField.vue';
  import FTSWeightField from '../FTSWeightField.vue';
  import FTSWeeklyGroupingFilter from './FTSWeeklyGroupingFilter.vue';
  import FTSWeeklyMovementOfGoodsFilter from './FTSWeeklyMovementOfGoodsFilter.vue';
  import FTSWeeklyProductsFilter from './FTSWeeklyProductsFilter.vue';
  import FTSWeeklyResponsibleOkatoField from './FTSWeeklyResponsibleOkatoField.vue';

  const getPreparedFilter = (filter: TFTSWeeklyFilterRequest): TFTSWeeklyFilter => ({
    ...filter,
    codes: filter.codes.join(CODE_SEPARATORS),
  });

  export default defineComponent({
    name: 'FTSWeeklyFilter',
    components: {
      FTSOkatoFilter,
      FTSWeeklyProductsFilter,
      FTSCountriesFilter,
      FTSFilterWrapper,
      FTSWeeklyPeriodField,
      FTSWeeklyResponsibleOkatoField,
      ComponentInput,
      FTSFilterWrapperToogle,
      FTSCumulativeTotalField,
      FTSAdditionalUnitsField,
      FTSWeeklyGroupingFilter,
      FTSPriceField,
      FTSWeightField,
      FTSTradeDirectionField,
      FTSWeeklyMovementOfGoodsFilter,
    },
    emits: {
      searchFilter: (filter: TFTSWeeklyFilterRequest) => filter,
      updateFilter: (filter: TFTSWeeklyFilterRequest) => filter,
    },
    mixins: [security],
    props: {
      defaultFilter: {
        type: Object as PropType<TFTSWeeklyFilterRequest>,
        required: true,
      },
      readonlyPermissionCode: {
        type: Number,
      },
      exportConfig: {
        type: Object as PropType<TExportConfig>,
        required: true,
      },
    },
    data() {
      return {
        filter: getPreparedFilter(this.defaultFilter),
        isReadonly: true,
      };
    },
    methods: {
      prepareFilter(filter: TFTSWeeklyFilter): TFTSWeeklyFilterRequest {
        return {
          ...filter,
          codes: splitCodes(filter.codes),
        };
      },
      searchFilter(filter: TFTSWeeklyFilter) {
        this.initFilter(filter);
        this.$emit('searchFilter', this.prepareFilter(filter));
      },
      initFilter(filter: TFTSWeeklyFilter) {
        this.filter = { ...filter };
      },
      loadFilter(loaded: TFTSWeeklyFilter) {
        this.filter = { ...getPreparedFilter(this.defaultFilter), ...loaded };
      },
      checkAdditionalValidation(filter: TFTSWeeklyFilter) {
        if (
          !filter.isStatisticsPrice &&
          !filter.isSumDollarsPayments &&
          !filter.isPriceProduct &&
          !filter.isWeightProduct &&
          !filter.isQuotaProduct
        ) {
          Constants.alert.fire(Constants.errorTitle, 'Выберите как минимум один показатель', 'error');
          return false;
        }

        return true;
      },
    },
    watch: {
      defaultFilter: {
        handler(newValue: TFTSWeeklyFilterRequest) {
          this.filter = getPreparedFilter(newValue);
        },
        immediate: true,
        deep: true,
      },
      filter: {
        handler(updatedFilter: TFTSWeeklyFilter) {
          this.$emit('updateFilter', this.prepareFilter(updatedFilter));
        },
        deep: true,
      },
      readonlyPermissionCode: {
        async handler(newValue: number) {
          if (newValue) {
            this.isReadonly = !(await this.checkPermission(newValue)).data;
          } else {
            this.isReadonly = false;
          }
        },
        immediate: true,
      },
    },
  });
